import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { BarPlot, ChartsAxisHighlight, ChartsLegend, ChartsXAxis, ChartsYAxis } from '@mui/x-charts';
import { ResponsiveChartContainer } from '@mui/x-charts/ResponsiveChartContainer';
import _ from 'lodash';
import { BoxWithHeader } from '../common';
import { StatisticsPeriod } from '../types/statistics';
import { DealerAccidentsOverviewProps } from './types';

const prepareTiperChartData = (periods: StatisticsPeriod[]) => {
  const allTipers = periods.flatMap((entry) =>
    entry.values.countAcceptedBySource.filter((source) => source.type === 'tip').flatMap((source) => source.values)
  );

  const aggregated = _(allTipers)
    .groupBy('id')
    .map((items, id) => ({
      id: Number(id),
      name: items[0].name,
      count: _.sumBy(items, 'count')
    }))
    .orderBy('count', 'desc')
    .take(10)
    .value();

  const dealerLabels = aggregated.map((item) => item.name);
  const totalCounts = aggregated.map((item) => item.count);

  return { dealerLabels, totalCounts };
};

export const DealerAccidentsBestTipers = ({ graphData, date }: DealerAccidentsOverviewProps) => {
  if (!graphData) return null;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { dealerLabels, totalCounts } = prepareTiperChartData(graphData.period);

  const formatName = (name?: string) => {
    if (!name) return '';
    if (!isMobile) return name;

    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0];

    const firstName = parts[0];
    const last = parts[parts.length - 1];
    const initial = last[0]?.toUpperCase() ?? '';

    return `${firstName} ${initial}.`;
  };

  const formattedDealerLabels = dealerLabels.map((label) => formatName(label));

  const layout = isMobile ? 'horizontal' : 'vertical';

  const xAxis = isMobile
    ? [
        {
          id: 'x-axis-id',
          scaleType: 'linear'
        } as const
      ]
    : [
        {
          id: 'x-axis-id',
          data: formattedDealerLabels,
          scaleType: 'band'
        } as const
      ];

  const yAxis = isMobile
    ? [
        {
          id: 'y-axis-id',
          data: formattedDealerLabels,
          scaleType: 'band'
        } as const
      ]
    : [
        {
          id: 'y-axis-id',
          scaleType: 'linear',
          tickMinStep: 1
        } as const
      ];

  const margin = isMobile
    ? { left: 45, right: 10, top: 20, bottom: 90 }
    : { left: 40, right: 20, top: 20, bottom: 150 };

  return (
    <BoxWithHeader
      title={
        <>
          <Typography variant="h6" fontWeight="bold">
            Přehled nejlepších tipařů obchodníka
          </Typography>
          <Typography variant="body2" color="gray">
            {`Top 10 nejlepších tipařů (${date})`}
          </Typography>
        </>
      }
    >
      <Box sx={{ width: '100%', height: 600 }}>
        <ResponsiveChartContainer
          skipAnimation
          margin={margin}
          series={[
            {
              type: 'bar',
              data: totalCounts,
              label: 'Počet tipů',
              color: '#C62327',
              layout
            }
          ]}
          yAxis={yAxis}
          xAxis={xAxis}
        >
          <BarPlot borderRadius={2} barLabel="value" />
          <ChartsXAxis
            position="bottom"
            axisId="x-axis-id"
            label={isMobile ? 'Počet tipů' : undefined}
            tickLabelStyle={
              isMobile
                ? {
                    fontSize: 10
                  }
                : {
                    angle: -45,
                    textAnchor: 'end',
                    fontSize: 10
                  }
            }
          />
          <ChartsYAxis
            position="left"
            axisId="y-axis-id"
            label={!isMobile ? 'Počet tipů' : undefined}
            tickLabelStyle={
              isMobile
                ? {
                    fontSize: 9,
                    angle: -40
                  }
                : {
                    textAnchor: 'end',
                    fontSize: 9
                  }
            }
          />
          <ChartsAxisHighlight y="band" />
          <ChartsLegend position={{ vertical: 'bottom', horizontal: 'middle' }} />
        </ResponsiveChartContainer>
      </Box>
    </BoxWithHeader>
  );
};
